/**
 * Chat API for Widget
 *
 * Provides chat operations for the widget.
 * Wraps Firestore operations for creating chats and sending messages.
 */

import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { getDb } from './firebase';
import type { WidgetMessage } from '../types/widget';

/**
 * Chat document structure (simplified for widget)
 */
interface ChatDoc {
  id: string;
  status: 'active' | 'closed';
  mode: 'bot' | 'agent';
  createdAt: Timestamp;
  lastMessageAt: Timestamp;
  endUser: {
    externalId?: string;
    visitorId?: string;

    firstName?: string;
    contactInformation?: string;

    name?: string;
    email?: string;
    phone?: string;

    isAuthenticated: boolean;
    currentPage?: string;
  };
  context: {
    initial: Record<string, unknown>;
  };
  rateLimitState?: {
    blockedUntil?: Timestamp;
    reason?: 'message_rate_limit';
    updatedAt?: Timestamp;
    scope?: 'chat';
  };
}

/**
 * Message document structure
 */
interface MessageDoc {
  id: string;
  senderType: 'endUser' | 'bot' | 'agent' | 'system';
  senderName?: string;
  content: string;
  createdAt: Timestamp;
  status: 'sent' | 'failed' | 'pending';
  context?: {
    currentPage?: string;
    url?: string;
    pageTitle?: string;
  };
}

type ImportMetaEnvLike = {
  VITE_FUNCTIONS_CUSTOM_DOMAIN?: string;
};

export class WidgetRateLimitError extends Error {
  code = 'RATE_LIMIT_EXCEEDED';
  retryAfterSeconds: number;
  retryAfterMinutes: number;
  blockedUntilTs?: number;

  constructor(
      message: string,
      retryAfterSeconds: number,
      retryAfterMinutes: number,
      blockedUntilTs?: number
  ) {
    super(message);
    this.name = 'WidgetRateLimitError';
    this.retryAfterSeconds = retryAfterSeconds;
    this.retryAfterMinutes = retryAfterMinutes;
    this.blockedUntilTs = blockedUntilTs;
  }
}

function getFunctionsBaseUrl(): string {
  return 'https://ai.feed-b.co.il';
}

/**
 * Find an active chat for a visitor
 */
export async function findActiveChat(
    accountId: string,
    agentId: string,
    visitorId: string
): Promise<ChatDoc | null> {
  const db = getDb();
  const chatsRef = collection(db, 'accounts', accountId, 'agents', agentId, 'chats');

  const q = query(
      chatsRef,
      where('endUser.externalId', '==', visitorId),
      where('status', '==', 'active'),
      orderBy('lastMessageAt', 'desc'),
      limit(1)
  );

  const snapshot = await getDocs(q);

  if (snapshot.empty) {
    return null;
  }

  const docSnap = snapshot.docs[0];
  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as ChatDoc;
}

/**
 * Create a new chat
 */
export async function createChat(
    accountId: string,
    agentId: string,
    visitorId: string,
    options?: {
      userFirstName?: string;
      userContactInformation?: string;
      currentPage?: string;
      initialContext?: Record<string, unknown>;
    }
): Promise<ChatDoc> {
  const db = getDb();
  const chatsRef = collection(db, 'accounts', accountId, 'agents', agentId, 'chats');

  const now = Timestamp.now();

  const firstName = options?.userFirstName || null;
  const contactInformation = options?.userContactInformation || null;

  const newChat = {
    createdAt: now,
    lastMessageAt: now,
    status: 'active' as const,
    mode: 'bot' as const,
    source: 'widget' as const,
    endUser: {
      externalId: visitorId,
      visitorId,

      firstName,
      contactInformation,

      name: firstName,
      email: contactInformation,

      isAuthenticated: false,
      currentPage: options?.currentPage || window.location.href,
    },
    context: {
      initial: {
        ...(options?.initialContext || {}),
        url: window.location.href,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        origin: window.location.origin,
      },
    },
    unreadForAgent: 0,
    unreadForEndUser: 0,
  };

  const docRef = await addDoc(chatsRef, newChat);

  console.log('[LongChat] Created new chat:', docRef.id);

  return {
    id: docRef.id,
    ...newChat,
  } as ChatDoc;
}

/**
 * Close an existing chat (non-destructive).
 *
 * Marks the chat document `status: 'closed'` using the existing status field.
 * This does NOT delete the chat or any messages — it only flips the status so
 * that `findActiveChat` / `openOrResumeChat` will no longer resume it. Allowed
 * for the anonymous widget user by the current Firestore security rules.
 */
export async function closeChat(
    accountId: string,
    agentId: string,
    chatId: string
): Promise<void> {
  const db = getDb();
  const chatRef = doc(db, 'accounts', accountId, 'agents', agentId, 'chats', chatId);
  await updateDoc(chatRef, {
    status: 'closed',
    lastMessageAt: Timestamp.now(),
  });
  console.log('[LongChat] Closed chat:', chatId);
}

/**
 * Open or resume a chat for a visitor
 */
export async function openOrResumeChat(
    accountId: string,
    agentId: string,
    visitorId: string,
    options?: {
      userFirstName?: string;
      userContactInformation?: string;
      initialContext?: Record<string, unknown>;
    }
): Promise<ChatDoc> {
  const existingChat = await findActiveChat(accountId, agentId, visitorId);

  if (existingChat) {
    console.log('[LongChat] Resuming existing chat:', existingChat.id);
    return existingChat;
  }

  return createChat(accountId, agentId, visitorId, options);
}

/**
 * Send a message through Cloud Function
 */
export async function sendMessage(
    accountId: string,
    agentId: string,
    chatId: string,
    visitorId: string,
    content: string,
    context?: {
      currentPage?: string;
      url?: string;
      pageTitle?: string;
    }
): Promise<string> {
  const endpoint = `${getFunctionsBaseUrl()}/sendWidgetMessage`;

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      accountId,
      agentId,
      chatId,
      visitorId,
      content,
      context: context || {
        url: window.location.href,
        pageTitle: document.title,
      },
    }),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const errorCode = data?.error?.code;
    const errorMessage = data?.error?.message || 'Failed to send message';

    if (errorCode === 'RATE_LIMIT_EXCEEDED') {
      throw new WidgetRateLimitError(
          errorMessage,
          data?.error?.retryAfterSeconds || 60,
          data?.error?.retryAfterMinutes || 1,
          data?.error?.blockedUntil
      );
    }

    throw new Error(errorMessage);
  }

  console.log('[LongChat] Message sent via function:', data.messageId);
  return data.messageId;
}

/**
 * Listen to a single chat document in real-time
 */
export function listenToChat(
    accountId: string,
    agentId: string,
    chatId: string,
    onChatChange: (chat: ChatDoc) => void,
    onError?: (error: Error) => void
): () => void {
  const db = getDb();
  const chatRef = doc(db, 'accounts', accountId, 'agents', agentId, 'chats', chatId);

  const unsubscribe = onSnapshot(
      chatRef,
      (snapshot) => {
        if (!snapshot.exists()) return;
        onChatChange({ id: snapshot.id, ...snapshot.data() } as ChatDoc);
      },
      (error) => {
        console.error('[LongChat] Error listening to chat:', error);
        onError?.(error);
      }
  );

  return unsubscribe;
}

/**
 * Listen to messages in real-time
 */
export function listenToMessages(
    accountId: string,
    agentId: string,
    chatId: string,
    onMessagesChange: (messages: WidgetMessage[]) => void,
    onError?: (error: Error) => void
): () => void {
  const db = getDb();
  const messagesRef = collection(
      db,
      'accounts',
      accountId,
      'agents',
      agentId,
      'chats',
      chatId,
      'messages'
  );

  const q = query(messagesRef, orderBy('createdAt', 'asc'));

  const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const messages: WidgetMessage[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data() as MessageDoc;

          return {
            id: docSnap.id,
            senderType: data.senderType,
            senderName: data.senderName,
            content: data.content,
            timestamp: data.createdAt?.toDate?.() || new Date(),
            status: data.status,
          };
        });

        onMessagesChange(messages);
      },
      (error) => {
        console.error('[LongChat] Error listening to messages:', error);
        onError?.(error);
      }
  );

  return unsubscribe;
}